import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import moment from 'moment';
import { companyIdAtom } from 'views/state';

import { EPType } from './config';
import {
  applyIntervalFilter,
  applyTimespanFilter,
  checkNonZeroArray,
  checkNonZeroObj,
  getAllIndexes,
  getNewDataGenerationDate,
  mergeArrays,
} from '../helper';
import {
  DashboardType,
  GroupingDataItem,
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimespanFilter,
  VulnerabilityStatus,
  VulnStatus,
} from '../types';

// DASHBOARD FILTERS
export const severityFiltersAtom = atomWithStorage<string[]>(
  'ep_dashboard_severity_filter',
  [
    Severity[Severity.Urgent],
    Severity[Severity.Critical],
    Severity[Severity.Serious],
    Severity[Severity.Medium],
    Severity[Severity.Minimal],
  ],
);
export const showHistoryChartCountsAtom = atomWithStorage(
  'ep_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const epRefreshAtom = atomWithStorage<string>('ep_refresh', '');
export const epCurrentVulnCountAtom = atomWithStorage<string>(
  'epCurrentVulnCount',
  '',
);

export const historyChartsOpenAtom = atom(true);
export const currentVulnChartsOpenAtom = atom(true);

export const epTypeAtom = atomWithReset(EPType.All);
export const appliedEPIdsAtom = atomWithReset<number[]>([]);
export const isObsoleteGroupingVulnListingOpenAtom = atom(false);
export const isGroupingByQidVulnListingOpenAtom = atom(false);

export const dashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const appliedEPIds = get(appliedEPIdsAtom);
  const refresh = get(epRefreshAtom);

  try {
    if (companyId && companyId !== -1) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = {
        company_id: companyId,
        host_type: DashboardType.EP,
        timezone: userTimezone,
      };
      if (appliedEPIds?.length > 0) {
        params['obj_id_list'] = appliedEPIds;
      }

      const responseData = await callApi('dashboard/', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});

export const hostCountsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);
  if (!data?.hosts_counts)
    return {
      [EPType.Internal]: 0,
      [EPType.External]: 0,
      [EPType.EC2]: 0,
    };
  return data.hosts_counts;
});

export const historyChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  return (
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.weeks, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    []
  );
});

export const newDataGenerationIndexAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  const weeks = get(historyChartLabelsAtom);
  const years =
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.years, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    [];

  const dateInfo = getNewDataGenerationDate(DashboardType.EP);
  const weekIndexes = getAllIndexes(weeks, dateInfo.week);
  let newDataGenerationIndex = -1;
  weekIndexes.forEach((i) => {
    if (years[i] === dateInfo.year) {
      newDataGenerationIndex = i;
    }
  });
  return newDataGenerationIndex;
});

export const historyChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);

  if (data) {
    const historyChartSeverityData = {};
    const historyChartLastIntervalSeverityData = {};
    for (const status in VulnStatus) {
      historyChartSeverityData[status] = getHistoryChartData(
        data.historical_data,
        status as VulnStatus,
        epType,
        severityFilters,
        timespanFilter,
        intervalFilter,
        'severity',
      );
      if (status !== VulnStatus.All) {
        historyChartLastIntervalSeverityData[status] =
          getHistoryChartIntervalData(
            data.historical_data,
            status as VulnStatus,
            epType,
            severityFilters,
            timespanFilter,
            intervalFilter,
            'last_interval_days',
          );
      }
    }

    return {
      historyChartSeverityData,
      historyChartLastIntervalSeverityData,
    };
  }
  return undefined;
});

export const vulnBySeverityChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(data.vulns_by_severity, epType);
  }
  return undefined;
});

export const obsoleteVulnGroupsChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  if (data) {
    return geObsoleteVulnGroupsData(data.obsolete_vuln_groups, epType);
  }
  return undefined;
});

export const patchableVulnBySeverityChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(data.patchable_vulns_by_severity, epType);
  }
  return undefined;
});

export const vulnGroupsChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnGroupsData(data.vuln_group, epType, severityFilters);
  }
  return undefined;
});

export const severityChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data?.vulns_by_severity) {
    return Object.keys(data.vulns_by_severity[EPType.Internal]);
  }
  return [];
});

export const vulnByStatusChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnByStatusChartData(
      data.vulns_by_status,
      epType,
      severityFilters,
    );
  }
  return undefined;
});

export const statusChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data?.vulns_by_status) {
    return Object.keys(
      data?.vulns_by_status[EPType.Internal][Severity[Severity.Urgent]],
    );
  }
  return [];
});

/*************************/
// HELPER FUNCTIONS
/*************************/

function getVulnByStatusChartData(
  vulnByStatusData: any,
  epType: EPType,
  severityFilters: string[],
) {
  const internal_values = vulnByStatusData[EPType.Internal];
  const external_values = vulnByStatusData[EPType.External];
  const ec2_values = vulnByStatusData[EPType.EC2];
  const dataArrays: number[][] = [];
  if (
    (epType === EPType.Internal || epType === EPType.All) &&
    checkNonZeroObj(internal_values)
  )
    dataArrays.push(internal_values);
  if (
    (epType === EPType.External || epType === EPType.All) &&
    checkNonZeroObj(external_values)
  )
    dataArrays.push(external_values);
  if (
    (epType === EPType.EC2 || epType === EPType.All) &&
    checkNonZeroObj(ec2_values)
  )
    dataArrays.push(ec2_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  const resultArrays: number[][] = [];
  dataArrays.forEach((dataset) => {
    const result = { ACTIVE: 0, NEW: 0, REOPENED: 0, FIXED: 0, IGNORED: 0 };
    selectedSeverityFilters.forEach((severity) => {
      const data = dataset[severity];
      if (data) {
        Object.values(VulnerabilityStatus).forEach((status) => {
          result[status] += data[status];
        });
      }
    });
    resultArrays.push(Object.values(result));
  });

  return resultArrays;
}

function getVulnGroupsData(
  vulnGroupsData: any,
  epType: EPType,
  severityFilters: string[],
) {
  const internal_data = vulnGroupsData[EPType.Internal];
  const external_data = vulnGroupsData[EPType.External];
  const ec2_data = vulnGroupsData[EPType.EC2];
  const dataArrays: any[] = [];
  if (epType === EPType.Internal || epType === EPType.All)
    dataArrays.push(internal_data);
  if (epType === EPType.External || epType === EPType.All)
    dataArrays.push(external_data);
  if (epType === EPType.EC2 || epType === EPType.All) dataArrays.push(ec2_data);

  const result: GroupingDataItem[] = [];

  // severity filter
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataArray) => {
    selectedSeverityFilters.forEach((severity) => {
      const dArray: GroupingDataItem[] = dataArray[severity];

      if (dArray) {
        dArray.forEach((item) => {
          // if already exists update counts
          const result_items = result.filter((x) => x.qid === item.qid);
          if (result_items?.length > 0) {
            result_items[0].count += item.count;
          }
          // otherwise insert a copy
          else {
            result.push({ ...item });
          }
        });
      }
    });
  });
  return result;
}

export const LastSyncDateAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data) {
    return moment(data.historical_data?.last_sync_date).format('lll');
  }
  return undefined;
});

export const vulnBySeverityChartDataCountAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.vulns_by_severity,
      epType,
      severityFilters,
    );
  }
  return undefined;
});

export const patchableVulnBySeverityChartDataCountAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const epType = get(epTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.patchable_vulns_by_severity,
      epType,
      severityFilters,
    );
  }
  return undefined;
});

function geObsoleteVulnGroupsData(vulnGroupsData: any, epType: EPType) {
  // sever or workstation filter
  const internal_data = vulnGroupsData[EPType.Internal];
  const external_data = vulnGroupsData[EPType.External];
  const ec2_data = vulnGroupsData[EPType.EC2];
  const dataArrays: any[] = [];
  if (epType === EPType.Internal || epType === EPType.All)
    dataArrays.push(internal_data);
  if (epType === EPType.External || epType === EPType.All)
    dataArrays.push(external_data);
  if (epType === EPType.EC2 || epType === EPType.All) dataArrays.push(ec2_data);

  const result: GroupingDataItem[] = [];
  dataArrays.forEach((dataArray) => {
    const dArray: GroupingDataItem[] = dataArray;

    if (dArray) {
      dArray.forEach((item) => {
        // if already exists update counts
        const result_items = result.filter((x) => x.qid === item.qid);
        if (result_items?.length > 0) {
          result_items[0].count += item.count;
        }
        // otherwise insert a copy
        else {
          result.push({ ...item });
        }
      });
    }
  });
  return result;
}

function getVulnBySeverityChartDataCount(
  sourceData: any,
  epType: EPType,
  severityFilters: string[],
) {
  const internal_values = sourceData[EPType.Internal];
  const external_values = sourceData[EPType.External];
  const ec2_values = sourceData[EPType.EC2];
  const dataArrays: number[][] = [];
  const resultsArray: number[][] = [];
  if (epType === EPType.Internal || epType === EPType.All)
    dataArrays.push(internal_values);
  if (epType === EPType.External || epType === EPType.All)
    dataArrays.push(external_values);
  if (epType === EPType.EC2 || epType === EPType.All)
    dataArrays.push(ec2_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataset) => {
    const result: number[] = [];
    const severities = ['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal'];
    severities.forEach((severity) => {
      if (selectedSeverityFilters.includes(severity)) {
        result.push(dataset[severity]);
      } else {
        result.push(0);
      }
    });
    resultsArray.push(Object.values(result));
  });
  let count = 0;
  resultsArray.forEach((dataset) => {
    count += dataset.reduce((partialSum, x) => partialSum + x, 0);
  });
  return count;
}

function getVulnBySeverityChartData(sourceData: any, epType: EPType) {
  const internal_values = Object.values<number>(sourceData[EPType.Internal]);
  const external_values = Object.values<number>(sourceData[EPType.External]);
  const ec2_values = Object.values<number>(sourceData[EPType.EC2]);
  const resultsArray: number[][] = [];
  if (
    (epType === EPType.Internal || epType === EPType.All) &&
    checkNonZeroArray(internal_values)
  ) {
    resultsArray.push(internal_values);
  }
  if (
    (epType === EPType.External || epType === EPType.All) &&
    checkNonZeroArray(external_values)
  ) {
    resultsArray.push(external_values);
  }

  if (
    (epType === EPType.EC2 || epType === EPType.All) &&
    checkNonZeroArray(ec2_values)
  ) {
    resultsArray.push(ec2_values);
  }
  return resultsArray;
}

function getHistoryChartData(
  historicalData: any,
  status: VulnStatus,
  epType: EPType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let internalsDataArray: number[][] = [];
  let externalsDataArray: number[][] = [];
  let ec2DataArray: number[][] = [];
  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  selectedSeverityFilters.forEach((severityFilter) => {
    internalsDataArray.push(
      historicalData[propertyName][severityFilter]?.Internal[status],
    );
    externalsDataArray.push(
      historicalData[propertyName][severityFilter]?.External[status],
    );
    ec2DataArray.push(
      historicalData[propertyName][severityFilter]?.EC2[status],
    );
  });

  let internalsData: number[] = mergeArrays(internalsDataArray);
  let externalsData: number[] = mergeArrays(externalsDataArray);
  let ec2Data: number[] = mergeArrays(ec2DataArray);
  const intervalFilterType =
    status === VulnStatus.All
      ? IntervalFilterType.SkipRecords
      : IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (epType === EPType.Internal || epType === EPType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(internalsData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );
  if (epType === EPType.External || epType === EPType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(externalsData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );

  if (epType === EPType.EC2 || epType === EPType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(ec2Data, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );

  return resultArray;
}

function getHistoryChartIntervalData(
  historicalData: any,
  status: VulnStatus,
  epType: EPType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let internalsDataArray: any[] = [];
  let externalsDataArray: any[] = [];
  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  let statusIntervalFilterKey = `${status.toLowerCase()}_${intervalFilter}`;

  selectedSeverityFilters.forEach((severityFilter) => {
    internalsDataArray.push(
      historicalData[propertyName][severityFilter]?.Internal[
        statusIntervalFilterKey
      ],
    );
    externalsDataArray.push(
      historicalData[propertyName][severityFilter]?.External[
        statusIntervalFilterKey
      ],
    );
  });

  let internalsData: number[] = internalsDataArray;
  let externalsData: number[] = externalsDataArray;
  const intervalFilterType = IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (epType === EPType.Internal || epType === EPType.All)
    resultArray.push(
      applyIntervalFilter(internalsData, intervalFilter, intervalFilterType),
    );
  if (epType === EPType.External || epType === EPType.All)
    resultArray.push(
      applyIntervalFilter(externalsData, intervalFilter, intervalFilterType),
    );

  return resultArray;
}

function getSelectedSeverityFilters(severityFilters: string[]) {
  let selectedSeverityFilters = severityFilters;
  if (severityFilters.includes(Severity[Severity.All])) {
    selectedSeverityFilters = [
      Severity[Severity.Urgent],
      Severity[Severity.Critical],
      Severity[Severity.Medium],
      Severity[Severity.Serious],
      Severity[Severity.Minimal],
    ];
  }
  return selectedSeverityFilters;
}
